// EcoFarmAgricultureLayout.js - A Beautiful, Different Agriculture Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-amber-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-amber-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function EcoFarmAgricultureLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#8B7D3C"; // Earthy olive
  const secondary = T.secondaryColor || "#C4A35A"; // Golden harvest
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isCloudAnimating, setIsCloudAnimating] = useState(true);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Farm hero images - DIRECTLY FROM CONFIG
  const farmHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/9e8a94eb-a37b-413f-884c-0426b057ac94_eco1.jpg",
    },
    {
      field: "ecoHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2b9d6027-617a-4654-b471-80a74ab4d7ae_eco2.webp",
    },
    {
      field: "ecoHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/603c6c3b-d7f7-4b77-8bae-9b741f81b62a_eco3.jpg",
    },
    {
      field: "ecoHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/31e02ffc-405f-4a6a-864c-9a1e764fcf43_eco4.jpg",
    },
  ];

  // Different farm products with organic feel - Added imageField
  const farmProducts = [
    // Organic Produce
    {
      id: 1,
      nameKey: "product1_name",
      name: "Organic Heirloom Tomatoes (1kg)",
      priceKey: "product1_price",
      price: 299,
      originalPriceKey: "product1_original_price",
      originalPrice: 399,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8aaa159e-f445-442d-bb77-93a07c979c49_Organic_Heirloom_Tomatoes.jpg",
      categoryKey: "product1_category",
      category: "Organic Produce",
      subCategory: "Vegetables",
      descriptionKey: "product1_description",
      description: "Sun-ripened heirloom tomatoes bursting with flavor. Grown using organic practices.",
      badgeKey: "product1_badge",
      badge: "Farm Fresh",
      badgeColor: "#8B7D3C",
      quantity: "1kg",
      brand: "EcoFarm",
      season: "Summer",
      origin: "Local Farm"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Organic Mixed Greens (250g)",
      priceKey: "product2_price",
      price: 199,
      originalPriceKey: "product2_original_price",
      originalPrice: 299,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1f321a25-5e17-4102-a077-d03d72787837_Organic_Mixed_Greens.jpg",
      categoryKey: "product2_category",
      category: "Organic Produce",
      subCategory: "Leafy Greens",
      descriptionKey: "product2_description",
      description: "Fresh organic mixed greens perfect for salads. Harvested daily.",
      badgeKey: "product2_badge",
      badge: "Premium",
      badgeColor: "#C4A35A",
      quantity: "250g",
      brand: "EcoFarm",
      season: "All Seasons",
      origin: "Local Farm"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Organic Honey (500ml)",
      priceKey: "product3_price",
      price: 449,
      originalPriceKey: "product3_original_price",
      originalPrice: 599,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/64e47a18-ed58-47d9-8aa5-44ed44612c52_Organic_Honey.jpg",
      categoryKey: "product3_category",
      category: "Organic Produce",
      subCategory: "Honey",
      descriptionKey: "product3_description",
      description: "Pure raw honey from our farm's beehives. Unfiltered and unpasteurized.",
      badgeKey: "product3_badge",
      badge: "Raw",
      badgeColor: "#D4A843",
      quantity: "500ml",
      brand: "EcoFarm",
      season: "All Seasons",
      origin: "Apiary"
    },
    
    // Farm Dairy
    {
      id: 4,
      nameKey: "product4_name",
      name: "Farm Fresh Milk (1L)",
      priceKey: "product4_price",
      price: 149,
      originalPriceKey: "product4_original_price",
      originalPrice: 199,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7e00332b-bb73-4f32-89ae-cbdee231bc2c_milkeco.jpg",
      categoryKey: "product4_category",
      category: "Dairy",
      subCategory: "Milk",
      descriptionKey: "product4_description",
      description: "Fresh milk from grass-fed cows. Pasteurized and bottled daily.",
      badgeKey: "product4_badge",
      badge: "Fresh",
      badgeColor: "#8B7D3C",
      quantity: "1L",
      brand: "EcoFarm",
      type: "Full Cream",
      origin: "Dairy Farm"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Artisan Farm Cheese (200g)",
      priceKey: "product5_price",
      price: 399,
      originalPriceKey: "product5_original_price",
      originalPrice: 549,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b6d614e8-993a-4084-81f4-11d0e8d83841_Artisan_Farm_Cheese.jpg",
      categoryKey: "product5_category",
      category: "Dairy",
      subCategory: "Cheese",
      descriptionKey: "product5_description",
      description: "Handcrafted farm cheese made with traditional methods. Aged to perfection.",
      badgeKey: "product5_badge",
      badge: "Artisan",
      badgeColor: "#C4A35A",
      quantity: "200g",
      brand: "EcoFarm",
      type: "Aged",
      origin: "Dairy Farm"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Organic Yogurt (500ml)",
      priceKey: "product6_price",
      price: 249,
      originalPriceKey: "product6_original_price",
      originalPrice: 349,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2d324b09-bb67-4e1e-bfd9-beb5564a575c_Organic_Yogurt.jpg",
      categoryKey: "product6_category",
      category: "Dairy",
      subCategory: "Yogurt",
      descriptionKey: "product6_description",
      description: "Creamy organic yogurt with live cultures. Perfect for breakfast or snacks.",
      badgeKey: "product6_badge",
      badge: "Probiotic",
      badgeColor: "#8B7D3C",
      quantity: "500ml",
      brand: "EcoFarm",
      type: "Greek Style",
      origin: "Dairy Farm"
    },
    
    // Farm Goods
    {
      id: 7,
      nameKey: "product7_name",
      name: "Organic Eggs (12 pack)",
      priceKey: "product7_price",
      price: 299,
      originalPriceKey: "product7_original_price",
      originalPrice: 399,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c40e55a7-1c15-405e-bec3-ba71bb4c61da_Organic_Eggs.jpg",
      categoryKey: "product7_category",
      category: "Farm Goods",
      subCategory: "Eggs",
      descriptionKey: "product7_description",
      description: "Free-range organic eggs from happy hens. Rich in omega-3.",
      badgeKey: "product7_badge",
      badge: "Free Range",
      badgeColor: "#D4A843",
      quantity: "12 pack",
      brand: "EcoFarm",
      type: "Organic",
      origin: "Poultry"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Heritage Grain Flour (2kg)",
      priceKey: "product8_price",
      price: 499,
      originalPriceKey: "product8_original_price",
      originalPrice: 699,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/55377162-cb16-4f29-a14e-6d00c6ab949d_Heritage_Flour.jpg",
      categoryKey: "product8_category",
      category: "Farm Goods",
      subCategory: "Grains",
      descriptionKey: "product8_description",
      description: "Stone-ground heritage grain flour. Perfect for artisan baking.",
      badgeKey: "product8_badge",
      badge: "Heritage",
      badgeColor: "#8B7D3C",
      quantity: "2kg",
      brand: "EcoFarm",
      type: "Whole Grain",
      origin: "Mill"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Organic Herb Garden Kit",
      priceKey: "product9_price",
      price: 599,
      originalPriceKey: "product9_original_price",
      originalPrice: 799,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8e36c889-7174-4e86-aa7b-dbca7456a9f2_Herb_Garden_Kit.jpg",
      categoryKey: "product9_category",
      category: "Farm Goods",
      subCategory: "Gardening",
      descriptionKey: "product9_description",
      description: "Complete herb garden kit with organic seeds and soil.",
      badgeKey: "product9_badge",
      badge: "Garden",
      badgeColor: "#C4A35A",
      quantity: "Kit",
      brand: "EcoFarm",
      type: "Herbs",
      origin: "Nursery"
    }
  ];

  // Farm categories - With editable labels
  const farmCategories = [
    { key: "all", icon: "🌿", labelKey: "cat_all", label: "All Goods", color: "#8B7D3C" },
    { key: "produce", icon: "🍅", labelKey: "cat_produce", label: "Produce", color: "#C4A35A" },
    { key: "dairy", icon: "🥛", labelKey: "cat_dairy", label: "Dairy", color: "#D4A843" },
    { key: "goods", icon: "🥚", labelKey: "cat_goods", label: "Farm Goods", color: "#8B7D3C" },
    { key: "garden", icon: "🌱", labelKey: "cat_garden", label: "Garden", color: "#C4A35A" },
  ];

  // Farm guides with seasonal focus - Added imageField
  const farmGuides = [
    {
      id: 1,
      titleKey: "guide1_title",
      title: "Seasonal Planting Guide",
      descKey: "guide1_desc",
      desc: "Know what to plant and when for year-round harvest.",
      imageField: "guide1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4ced05ae-412b-41e3-b3c1-2fb5c419a24f_Seasonal_Planting_Guide.jpg",
      steps: [
        "Understand your growing zone",
        "Plan crop rotation schedule",
        "Prepare soil for each season",
        "Choose companion plants",
        "Harvest at optimal time"
      ],
      difficulty: "Beginner"
    },
    {
      id: 2,
      titleKey: "guide2_title",
      title: "Natural Pest Control",
      descKey: "guide2_desc",
      desc: "Eco-friendly pest management for organic farms.",
      imageField: "guide2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c5d40194-f515-4d54-8e7d-2717c1f00928_Natural_Pest_Control.avif",
      steps: [
        "Attract beneficial insects",
        "Use companion planting",
        "Create natural barriers",
        "Apply organic sprays",
        "Monitor regularly"
      ],
      difficulty: "Intermediate"
    },
    {
      id: 3,
      titleKey: "guide3_title",
      title: "Soil Health Management",
      descKey: "guide3_desc",
      desc: "Build healthy soil for sustainable farming.",
      imageField: "guide3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2042c42f-74af-4c46-b7d7-04d19c8821b0_Soil_Health_Management.jpg",
      steps: [
        "Test soil composition",
        "Add organic matter",
        "Practice crop rotation",
        "Use cover crops",
        "Maintain soil pH"
      ],
      difficulty: "Advanced"
    }
  ];

  // Farm journal posts - Added imageField
  const farmPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Art of Organic Farming",
      descKey: "post1_desc",
      desc: "Discover the beauty and benefits of organic farming methods.",
      fullContentKey: "post1_content",
      fullContent: "Organic farming is more than just a method—it's a philosophy that respects the land, the ecosystem, and the community. By working with nature rather than against it, organic farmers create sustainable food systems that nourish both people and the planet. From composting to crop rotation, learn how these ancient practices are shaping the future of agriculture.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e46985f3-25f0-41a6-843f-a49a7412d6bc_The_Art_of_Organic_Farming.jpg",
      dateKey: "post1_date",
      date: "June 15, 2024",
      authorKey: "post1_author",
      author: "Emma Greenfield",
      categoryKey: "post1_category",
      category: "Organic Farming"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Farm-to-Table Movement",
      descKey: "post2_desc",
      desc: "How local food systems are revolutionizing eating.",
      fullContentKey: "post2_content",
      fullContent: "The farm-to-table movement is reshaping how we think about food. By shortening the distance between farm and plate, we're creating more resilient food systems, supporting local economies, and enjoying fresher, more nutritious food. Explore how this movement is transforming communities and creating a more sustainable food future.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6bd06751-1416-4269-b293-f3c72e6d4254_Farm-to-Table_Movement.jpg",
      dateKey: "post2_date",
      date: "June 10, 2024",
      authorKey: "post2_author",
      author: "Marcus Rivera",
      categoryKey: "post2_category",
      category: "Food Systems"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Sustainable Agriculture Practices",
      descKey: "post3_desc",
      desc: "Innovative methods for a greener future.",
      fullContentKey: "post3_content",
      fullContent: "Sustainable agriculture is evolving with innovative approaches that balance productivity with environmental stewardship. From regenerative farming to agroforestry, discover the practices that are helping farmers produce food while protecting our natural resources for future generations.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/720582fd-a54c-491b-97b3-8ae85b181ada_Sustainable_Agriculture.jpeg",
      dateKey: "post3_date",
      date: "June 5, 2024",
      authorKey: "post3_author",
      author: "Dr. Sarah Chen",
      categoryKey: "post3_category",
      category: "Sustainability"
    }
  ];

  // Navigation items - With editable labels
  const navItems = [
    { key: "nav_home", labelKey: "nav_home", label: "HOME", page: "home" },
    { key: "nav_produce", labelKey: "nav_produce", label: "PRODUCE", page: "produce" },
    { key: "nav_dairy", labelKey: "nav_dairy", label: "DAIRY", page: "dairy" },
    { key: "nav_goods", labelKey: "nav_goods", label: "FARM GOODS", page: "goods" },
    { key: "nav_guides", labelKey: "nav_guides", label: "GUIDES", page: "guides" },
    { key: "nav_journal", labelKey: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", labelKey: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages - With editable fields
  const topBarMessages = [
    { key: "top_bar_message_1", value: "🌿 FRESH FROM OUR FARM" },
    { key: "top_bar_message_2", value: "✦ ORGANIC & SUSTAINABLE" },
    { key: "top_bar_message_3", value: "🌱 FARM TO TABLE PRODUCE" },
    { key: "top_bar_message_4", value: "✦ FREE DELIVERY ON ORDERS OVER ₹500" },
  ];

  // Floating particles (leaves and nature elements)
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 4 + Math.random() * 8,
    delay: Math.random() * 8,
    duration: 8 + Math.random() * 8,
    opacity: 0.05 + Math.random() * 0.12,
    rotation: Math.random() * 360,
    type: Math.random() > 0.5 ? 'leaf' : 'sparkle'
  }));

  // Floating clouds
  const clouds = Array.from({ length: 4 }, (_, i) => ({
    id: i,
    left: 10 + Math.random() * 60,
    top: 5 + Math.random() * 30,
    width: 100 + Math.random() * 200,
    height: 30 + Math.random() * 40,
    delay: i * 3,
    duration: 20 + Math.random() * 15,
    opacity: 0.1 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % farmHeroImageConfigs.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products with nature-inspired timing
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 400);
    
    products.forEach((_, index) => {
      const delay = index * 100 + 600;
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, delay);
    });
  }, [p.filteredProducts]);

  // Mouse move for organic parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 15,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openGuideModal = useCallback((guide, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedGuide(guide);
  }, [p.previewEditMode, p.activeEditField]);

  const closeGuideModal = useCallback(() => {
    setSelectedGuide(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedGuide(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || farmHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = farmHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, farmHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "ECO FARM",
    title: p.storeTheme?.heroTitle || "Growing Goodness, Naturally",
    subtitle: p.storeTheme?.heroSubtitle || "Sustainable farming for a healthier tomorrow",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop the Farm",
    secondaryCta: p.storeTheme?.secondaryCtaText || "Explore Guides",
  };

  // Get editable products with imageField
  const editableFarmProducts = farmProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable guides with imageField
  const editableFarmGuides = farmGuides.map((guide) => ({
    ...guide,
    image: p.storeTheme?.[guide.imageField] || guide.image,
  }));

  // Get editable posts with imageField
  const editableFarmPosts = farmPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items
  const getFilteredItems = (category) => {
    if (category === "all") return editableFarmProducts;
    const map = {
      "produce": "Organic Produce",
      "dairy": "Dairy",
      "goods": "Farm Goods",
      "garden": "Farm Goods",
    };
    return editableFarmProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get organic card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -50, y: -20, rotate: -5, scale: 0.9 },
      { x: 50, y: -20, rotate: 5, scale: 0.9 },
      { x: -50, y: 20, rotate: -3, scale: 0.9 },
      { x: 50, y: 20, rotate: 3, scale: 0.9 },
    ];
    return directions[index % directions.length];
  };

  // Seasonal items for home page - With editable labels
  const seasonalItems = [
    { icon: "🌸", titleKey: "season1_title", title: "Spring", descKey: "season1_desc", desc: "Asparagus, Strawberries, Peas" },
    { icon: "☀️", titleKey: "season2_title", title: "Summer", descKey: "season2_desc", desc: "Tomatoes, Corn, Zucchini" },
    { icon: "🍂", titleKey: "season3_title", title: "Fall", descKey: "season3_desc", desc: "Pumpkins, Apples, Squash" },
    { icon: "❄️", titleKey: "season4_title", title: "Winter", descKey: "season4_desc", desc: "Kale, Citrus, Root Veggies" },
  ];

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-amber-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'DM Sans', sans-serif" }}>
      
      {/* Nature Particles - Leaves and Sparkles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute ${particle.type === 'leaf' ? 'leaf-particle' : 'sparkle-particle'}`}
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: particle.type === 'leaf' 
                ? `radial-gradient(circle, ${primary}${Math.floor(30 + Math.random() * 30).toString(16)}, transparent)` 
                : `radial-gradient(circle, ${secondary}${Math.floor(30 + Math.random() * 30).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
              transform: `rotate(${particle.rotation}deg)`,
            }}
          />
        ))}
      </div>

      {/* Floating Clouds */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {clouds.map((cloud) => (
          <div
            key={cloud.id}
            className="absolute cloud-float"
            style={{
              left: `${cloud.left}%`,
              top: `${cloud.top}%`,
              width: `${cloud.width}px`,
              height: `${cloud.height}px`,
              animationDelay: `${cloud.delay}s`,
              animationDuration: `${cloud.duration}s`,
              opacity: cloud.opacity,
            }}
          >
            <div className="w-full h-full rounded-full bg-white/30 blur-3xl" />
          </div>
        ))}
      </div>
      
      {/* Top Bar - Organic Farm Style - All messages editable */}
      <div className="relative bg-[#5C4B3A] text-amber-200 py-2.5 overflow-hidden border-b-2 border-amber-700/30">
        <div className="flex items-center justify-center gap-4 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg) => (
            <span key={msg.key} className="inline-block mx-6 text-xs tracking-wider">
              <span className="mr-2">🌾</span>
              <InlineEditableTextWithNav 
                p={p} 
                field={msg.key} 
                value={msg.value}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Earthy Farm Style - All labels editable */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[#5C4B3A]/95 backdrop-blur-xl shadow-lg py-2 border-b border-amber-700/20" : "bg-[#5C4B3A]/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center text-xl border-2 border-amber-400/50 shadow-lg shadow-amber-700/20">
              🌾
            </div>
            <span className="font-bold text-xl tracking-tight text-amber-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Eco Farm"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-amber-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-amber-300" 
                    : "text-amber-100/80 hover:text-amber-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.labelKey} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-amber-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-amber-800/50">
                <FaHeart className="text-lg text-amber-100/80 hover:text-amber-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#5C4B3A] shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-amber-800/30">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-amber-800/30 text-amber-300" 
                    : "text-amber-100/80 hover:text-amber-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.labelKey} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - Farm Landscape with Organic Shapes - FIXED */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {farmHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Farm ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * 0.4}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#5C4B3A]/80 via-[#5C4B3A]/40 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-amber-50 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Organic wave overlay */}
              <div className="absolute bottom-0 left-0 right-0 z-20">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                  <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#FFFBEB" opacity="0.9" />
                </svg>
              </div>
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-[#5C4B3A]/40 backdrop-blur-md rounded-full border border-amber-400/30">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                    <span className="text-amber-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-amber-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("produce", e)} 
                    className="group px-8 py-4 rounded-full bg-amber-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="produce"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("guides", e)} 
                    className="px-8 py-4 rounded-full border-2 border-amber-400/50 text-amber-200 font-semibold tracking-wide transition-all duration-300 hover:bg-amber-600/30 hover:border-amber-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-amber-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE THE FARM"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-amber-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-amber-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Farm Badge */}
          <div className="relative z-10 -mt-0 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-amber-200">
              <span className="text-amber-600 text-sm">🌾</span>
              <span className="text-[#5C4B3A] text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="FROM OUR FARM TO YOUR TABLE"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-amber-600 text-sm">🌾</span>
            </div>
          </div>
          
          {/* Seasonal Farm Calendar - All labels editable */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="text-center mb-8">
              <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="seasonal_badge" 
                  value="Seasonal Harvest"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#5C4B3A] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="seasonal_title" 
                  value="What's Growing Now"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {seasonalItems.map((season, idx) => (
                <div 
                  key={idx} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-amber-200 hover:border-amber-400">
                    <div className="text-4xl mb-2 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                      {season.icon}
                    </div>
                    <h3 className="font-semibold text-[#5C4B3A] text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={season.titleKey} 
                        value={season.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={season.descKey} 
                        value={season.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Circular Category Navigation - All labels editable */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="flex flex-wrap justify-center gap-6">
              {farmCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("produce", e);
                    } else {
                      setActiveCategory(cat.key);
                      const pageMap = {
                        "produce": "produce",
                        "dairy": "dairy",
                        "goods": "goods",
                        "garden": "goods",
                      };
                      navigateTo(pageMap[cat.key] || "produce", e);
                    }
                  }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white shadow-lg border-2 border-amber-200 group-hover:border-amber-400 transition-all duration-500 group-hover:scale-110 group-hover:shadow-2xl flex items-center justify-center text-3xl md:text-4xl">
                      {cat.icon}
                    </div>
                    <span className="mt-2 text-xs font-medium text-[#5C4B3A] group-hover:text-amber-700 transition-colors">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cat.labelKey} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="produce"
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - Nature Card Style - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8 bg-white/70 backdrop-blur-sm border-y-2 border-amber-200/30">
            <div className="text-center mb-10">
              <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Fresh from the Farm"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#5C4B3A] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Today's Harvest"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableFarmProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0 scale-100" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg] scale-${entrance.scale}`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-amber-200 hover:border-amber-400">
                      <div className="relative h-48 overflow-hidden bg-amber-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            <span className="mr-1">🌿</span>
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={product.badgeKey} 
                              value={product.badge}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-[#5C4B3A]/80 text-amber-100 text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-[#5C4B3A] text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-amber-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("produce");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-[#5C4B3A] text-amber-100 font-semibold hover:bg-[#4A3D30] transition-all duration-300 hover:scale-105 shadow-lg shadow-[#5C4B3A]/20"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="See All Farm Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="produce"
                />
              </button>
            </div>
          </section>
          
          {/* Farm Guides Preview - Leaf Style Cards - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-8">
              <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="guides_badge" 
                  value="Learn from the Land"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#5C4B3A] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="guides_title" 
                  value="Farming Wisdom"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableFarmGuides.map((guide, idx) => (
                <div 
                  key={guide.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-amber-200 hover:border-amber-400 animate-card-fly"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => openGuideModal(guide)}
                >
                  <div className="h-40 overflow-hidden relative">
                    <InlineEditableImage p={p} field={guide.imageField} className="block h-full w-full overflow-hidden">
                      <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#5C4B3A]/60 to-transparent pointer-events-none" />
                    <div className="absolute bottom-2 left-3 text-white text-xs font-semibold flex items-center gap-1">
                      <span>🌱</span>
                      {guide.difficulty}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-[#5C4B3A] text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.titleKey} 
                        value={guide.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </h4>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.descKey} 
                        value={guide.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </p>
                    <div className="mt-3 text-amber-700 text-xs font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_guide_button" 
                        value="Learn More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== PRODUCE PAGE ==================== */}
      {activePage === "produce" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="produce_badge" 
                value="Organic Produce"
                onNavigate={handleNavigateAfterEdit}
                navPage="produce"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4B3A] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="produce_title" 
                value="Farm Fresh Vegetables"
                onNavigate={handleNavigateAfterEdit}
                navPage="produce"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="produce_subtitle" 
                value="Harvested daily from our organic fields"
                onNavigate={handleNavigateAfterEdit}
                navPage="produce"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableFarmProducts.filter(d => d.category === "Organic Produce").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-amber-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      <span className="mr-1">🌿</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={product.badgeKey} 
                        value={product.badge}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="produce"
                      />
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-[#5C4B3A]/80 text-amber-100 text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-[#5C4B3A] text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="produce"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-amber-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="produce"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <span>🌱</span> Season: {product.season}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== DAIRY PAGE ==================== */}
      {activePage === "dairy" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="dairy_badge" 
                value="Farm Dairy"
                onNavigate={handleNavigateAfterEdit}
                navPage="dairy"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4B3A] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="dairy_title" 
                value="Fresh Dairy Products"
                onNavigate={handleNavigateAfterEdit}
                navPage="dairy"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="dairy_subtitle" 
                value="From our grass-fed cows to your table"
                onNavigate={handleNavigateAfterEdit}
                navPage="dairy"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableFarmProducts.filter(d => d.category === "Dairy").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-amber-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      <span className="mr-1">🥛</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={product.badgeKey} 
                        value={product.badge}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="dairy"
                      />
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-[#5C4B3A]/80 text-amber-100 text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-[#5C4B3A] text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="dairy"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-amber-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="dairy"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <span>🐄</span> {product.type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== FARM GOODS PAGE ==================== */}
      {activePage === "goods" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="goods_badge" 
                value="Farm Goods"
                onNavigate={handleNavigateAfterEdit}
                navPage="goods"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4B3A] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="goods_title" 
                value="Artisan Farm Products"
                onNavigate={handleNavigateAfterEdit}
                navPage="goods"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="goods_subtitle" 
                value="Handcrafted with care from our farm"
                onNavigate={handleNavigateAfterEdit}
                navPage="goods"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableFarmProducts.filter(d => d.category === "Farm Goods").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-amber-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      <span className="mr-1">🌾</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={product.badgeKey} 
                        value={product.badge}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="goods"
                      />
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-[#5C4B3A]/80 text-amber-100 text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-[#5C4B3A] text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="goods"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-amber-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="goods"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <span>🏷️</span> {product.type || "Farm Fresh"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== GUIDES PAGE ==================== */}
      {activePage === "guides" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_badge" 
                value="Guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4B3A] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_title" 
                value="Farming Knowledge"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_subtitle" 
                value="Expert advice for sustainable farming"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableFarmGuides.map((guide, idx) => (
              <div 
                key={guide.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.1}s` }}
                onClick={() => openGuideModal(guide)}
              >
                <div className="h-56 overflow-hidden relative">
                  <InlineEditableImage p={p} field={guide.imageField} className="block h-full w-full overflow-hidden">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#5C4B3A]/60 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-4">
                    <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full font-semibold">
                      {guide.difficulty}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#5C4B3A] mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.titleKey} 
                      value={guide.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.descKey} 
                      value={guide.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </p>
                  <div className="mt-4 text-amber-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="read_guide_button" 
                      value="Read Guide"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#5C4B3A] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4B3A] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Farm Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Insights from our sustainable farm"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableFarmPosts.map((post, idx) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.1}s` }}
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-amber-700 font-semibold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.categoryKey} 
                        value={post.category}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#5C4B3A] mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-[#5C4B3A]">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-amber-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#5C4B3A] text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Our Farm"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#5C4B3A] mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Nurturing Nature, Growing Goodness"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-amber-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Eco Farm is a sustainable agricultural venture dedicated to producing high-quality organic food while nurturing the land for future generations. Our farm practices regenerative agriculture, focusing on soil health, biodiversity, and ecological balance."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="From our family to yours, we believe in transparency, quality, and community. Every product you receive from us tells a story of care, sustainability, and dedication to the craft of farming. We're proud to be your local farm connection."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Organic Varieties"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="200+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="15+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Years of Farming"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
              <InlineEditableImage p={p} field="ecoAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.ecoAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/9e8a94eb-a37b-413f-884c-0426b057ac94_eco1.jpg"} 
                  alt="About Eco Farm" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🌿 Shop Farm"],
            ["services", "🚜 Services"],
            ["details", "📖 About Farm"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-[#5C4B3A] text-amber-100 shadow-lg" : "text-gray-500 hover:text-[#5C4B3A]"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
    
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  <span className="mr-1">🌿</span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.badgeKey} 
                    value={selectedProduct.badge}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-[#5C4B3A]/80 text-amber-100 text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-[#5C4B3A]">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full border border-amber-200">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.season && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    🌱 {selectedProduct.season}
                  </span>
                )}
                {selectedProduct.origin && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    📍 {selectedProduct.origin}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-amber-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-amber-600 text-center bg-amber-50 p-3 rounded-full border border-amber-200">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_message" 
                  value="🌿 Fresh from our farm to your table"
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Guide Modal - FIXED with InlineEditableImage */}
      {selectedGuide && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-amber-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#5C4B3A]">Farming Guide</h3>
              <button 
                onClick={closeGuideModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedGuide.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedGuide.image} alt={selectedGuide.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-full font-semibold border border-amber-200">
                  {selectedGuide.difficulty}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#5C4B3A] mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.titleKey} 
                  value={selectedGuide.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </h2>
              <p className="text-gray-600 mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.descKey} 
                  value={selectedGuide.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </p>
              <div className="space-y-4">
                <p className="font-semibold text-[#5C4B3A]">Steps:</p>
                {selectedGuide.steps?.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <span className="w-8 h-8 rounded-full bg-[#5C4B3A] text-amber-100 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeGuideModal} 
                  className="px-6 py-2 rounded-full bg-[#5C4B3A] text-amber-100 hover:bg-[#4A3D30] transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="guides"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-amber-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#5C4B3A]">Farm Journal</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-full font-semibold border border-amber-200">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.categoryKey} 
                    value={selectedPost.category}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#5C4B3A] mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-amber-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-[#5C4B3A]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-amber-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-[#5C4B3A] text-amber-100 hover:bg-[#4A3D30] transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes leaf-particle { 
          0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0.05; }
          25% { transform: translate(20px, -30px) rotate(90deg) scale(1.2); opacity: 0.15; }
          50% { transform: translate(-10px, -60px) rotate(180deg) scale(0.8); opacity: 0.1; }
          75% { transform: translate(15px, -40px) rotate(270deg) scale(1.1); opacity: 0.15; }
          100% { transform: translate(0, -80px) rotate(360deg) scale(1); opacity: 0.05; }
        }
        .leaf-particle { animation: leaf-particle 8s ease-in-out infinite; }
        
        @keyframes sparkle-particle {
          0% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1.5) rotate(180deg); opacity: 0.2; }
          100% { transform: scale(0) rotate(360deg); opacity: 0; }
        }
        .sparkle-particle { animation: sparkle-particle 6s ease-in-out infinite; border-radius: 50%; }
        
        @keyframes cloud-float {
          0% { transform: translateX(0px); }
          50% { transform: translateX(60px); }
          100% { transform: translateX(0px); }
        }
        .cloud-float { animation: cloud-float 20s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9) rotate(-2deg); } 60% { transform: translateY(-5px) scale(1.02) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}